import type { HookAccent, HookDesign } from '@reels/shared/constants';
import { memo, useEffect, useState } from 'react';
import { composeHook, HOOK_ACCENT, HOOK_FONT, HOOK_PAIR_FONT, HOOK_SCALE_X } from '@reels/shared/hook';
import type { SubtitlePosition } from '@reels/shared/typography';

export const HookOverlay = memo(function HookOverlay({ text, position, subtitlesEnabled, accent = HOOK_ACCENT, design = 'card', accentMode = 'normal', fontFamily = HOOK_FONT.family, hookPosition = null, box = true, thumbnail = false }: {
  text: string; position: SubtitlePosition; subtitlesEnabled: boolean; accent?: string; design?: HookDesign;
  accentMode?: HookAccent;
  /** Семейство хука совпадает со шрифтом субтитров, как в рендере. */
  fontFamily?: string;
  /** Ручное положение хука, см. hookLayout. */
  hookPosition?: number | null;
  /** Плашка хука: белая или чёрная вслед за плашкой субтитров, false — без неё. */
  box?: boolean | 'white' | 'black';
  thumbnail?: boolean;
}) {
  const [composition, setComposition] = useState<ReturnType<typeof composeHook> | null>(null);
  const [ascents, setAscents] = useState<number[]>([]);
  useEffect(() => {
    let active = true;
    const update = () => {
      if (!active) return;
      const context = document.createElement('canvas').getContext('2d');
      if (!context) return;
      const measure = (value: string, size: number, font?: 'pair') => {
        context.font = `900 100px "${font === 'pair' ? HOOK_PAIR_FONT.family : fontFamily}"`;
        const m = context.measureText(value);
        return { width: m.width * size / 100, height: (m.actualBoundingBoxAscent + m.actualBoundingBoxDescent) * size / 100,
          ascent: m.actualBoundingBoxAscent * size / 100, descent: m.actualBoundingBoxDescent * size / 100 };
      };
      const next = composeHook(text, 1080, 1920, measure, position, subtitlesEnabled, design, accentMode, hookPosition, box);
      setComposition(next);
      setAscents(next.lines.map(line => measure(line.text, line.size, line.font).ascent));
    };
    void Promise.all([document.fonts.load(`900 100px "${fontFamily}"`), document.fonts.load(`900 100px "${HOOK_PAIR_FONT.family}"`)]).then(update, update);
    // Шрифт мог догрузиться позже (медленная сеть): иначе плашки остаются по меркам запасного шрифта.
    document.fonts.addEventListener('loadingdone', update);
    return () => { active = false; document.fonts.removeEventListener('loadingdone', update); };
  }, [text, position, subtitlesEnabled, design, accentMode, fontFamily, hookPosition, box]);
  if (!composition) return null;
  const { card } = composition;
  return <svg className={thumbnail ? 'hook-thumbnail' : 'hook'} viewBox={thumbnail ? `${card.x - 18} ${card.y - 18} ${card.width + 36} ${card.height + 36}` : '0 0 1080 1920'} role="img" aria-label={text}>
    <g aria-hidden="true">
      {composition.shapes.map((shape, i) => <path key={i} d={shape.path}
        fill={shape.accent ? accent : shape.color ?? '#18181B'} fillOpacity={shape.opacity}
        stroke={shape.strokeAccent ? accent : 'white'} strokeWidth={shape.outlineWidth} strokeOpacity={shape.strokeAccent ? 1 : 0.55} />)}
      {composition.lines.map((line, i) => {
        const paint = { fontFamily: `"${line.font === 'pair' ? HOOK_PAIR_FONT.family : fontFamily}"`, fontWeight: 900, fontSize: line.size, fill: line.accent ? accent : line.color ?? 'white' };
        // «Волна»: буквы стоят на кривой, каждая повёрнута вокруг своей точки, как в рендере.
        if (line.glyphs) return <g key={i}>{line.glyphs.map((glyph, j) => <text key={j} x="0" y="0" {...paint}
          transform={`rotate(${-glyph.angle} ${glyph.ox} ${glyph.oy}) translate(${glyph.x} ${glyph.y + ascents[i]!}) scale(${HOOK_SCALE_X} 1)`}>{glyph.text}</text>)}</g>;
        return <text key={i} x="0" y="0" {...paint}
          transform={`translate(${line.x} ${line.y + ascents[i]!}) scale(${HOOK_SCALE_X} 1)`}>{line.text}</text>;
      })}
    </g>
  </svg>;
});
